import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getDataSource } from '.';
import { request } from '../api/client';

/** Server-state hooks (TanStack Query). Realtime events never invalidate these globally. */
const ds = () => getDataSource();

export const useSummary = () =>
  useQuery({ queryKey: ['summary'], queryFn: async () => (await ds()).summary() });
export const useSites = () =>
  useQuery({ queryKey: ['sites'], queryFn: async () => (await ds()).listSites(), staleTime: 60_000 });
export const useDevice = (id: string) =>
  useQuery({ queryKey: ['device', id], queryFn: async () => (await ds()).getDevice(id) });
export const useGateways = () =>
  useQuery({ queryKey: ['gateways'], queryFn: async () => (await ds()).listGateways() });
export const useAlarms = (enabled = true) =>
  useQuery({ queryKey: ['alarms'], queryFn: async () => (await ds()).listAlarms(), enabled });

export function useDevicePages(search: string, siteId?: string) {
  return useInfiniteQuery({
    queryKey: ['devices', { search, siteId }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      (await ds()).listDevices({ search, siteId, cursor: pageParam, limit: 200 }),
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useSiteNames() {
  const sites = useSites();
  return new Map((sites.data ?? []).map((s) => [s.id, s.name]));
}

/* ---------- per-device connection history (GET /api/v1/devices/:id/connections) ---------- */

export type ConnectionDisconnectReason =
  | 'superseded'
  | 'idle_timeout'
  | 'gateway_restart'
  | 'gateway_shutdown'
  | 'not_in_snapshot'
  | 'peer_closed'
  | 'detect_timeout'
  | 'protocol_error'
  | 'resource_limit'
  | 'unknown';

export interface ConnectionNetwork {
  rat?: 'LTE' | 'GSM' | 'NONE' | string;
  mcc?: string;
  mnc?: string;
  plmn?: string;
  cell_id?: string | number;
  tac?: string | number;
  lac?: string | number;
  pci?: string | number;
  earfcn?: string | number;
  band?: string | number;
  arfcn?: string | number;
  rsrp_dbm?: number;
  rsrq_db?: number;
  rssi_dbm?: number;
  sinr_db?: number;
  rxlev_dbm?: number;
  state?: string;
  signal?: number;
  imsi?: string;
  raw_servingcell?: string;
}

export interface ConnectionLogin {
  raw?: Record<string, string>;
  network?: ConnectionNetwork;
  access_time_s?: number;
}

export interface ConnectionCounters {
  rx_bytes: number;
  tx_bytes: number;
  rx_segments: number;
  tx_segments: number;
}

/** View model of one connection (camelCase); the wire row is api/v1 `DeviceConnection` (snake_case). */
export interface DeviceConnection {
  id: string;
  connectedAt: string;
  identifiedAt: string | null;
  disconnectedAt: string | null;
  durationS: number | null;
  disconnectReason: ConnectionDisconnectReason | string | null;
  remoteAddr: string | null;
  localPort: number | null;
  protocol: string | null;
  firmware: string | null;
  login: ConnectionLogin | null;
  counters: ConnectionCounters | null;
  previousDisconnectReason: string | null;
  gapSincePreviousS: number | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Final session counters, or null when the gateway reported none (the contract sends `{}` in that case). */
function counters(v: unknown): ConnectionCounters | null {
  if (!isRecord(v) || Object.keys(v).length === 0) return null;
  return {
    rx_bytes: num(v.rx_bytes) ?? 0,
    tx_bytes: num(v.tx_bytes) ?? 0,
    rx_segments: num(v.rx_segments) ?? 0,
    tx_segments: num(v.tx_segments) ?? 0,
  };
}

/**
 * One wire row → view model. Accepts the contract's snake_case names (authoritative) and their camelCase spelling,
 * so the card does not depend on which one a backend build sends.
 */
export function normalizeConnection(v: unknown): DeviceConnection {
  if (!isRecord(v) || typeof v.id !== 'string') throw new Error('invalid connection item');
  const pick = (snake: string, camel: string) => (v[snake] !== undefined ? v[snake] : v[camel]);
  const connectedAt = str(pick('connected_at', 'connectedAt'));
  if (!connectedAt) throw new Error('invalid connection item');
  const login = pick('login', 'login');
  return {
    id: v.id,
    connectedAt,
    identifiedAt: str(pick('identified_at', 'identifiedAt')),
    disconnectedAt: str(pick('disconnected_at', 'disconnectedAt')),
    durationS: num(pick('duration_s', 'durationS')),
    disconnectReason: str(pick('disconnect_reason', 'disconnectReason')),
    remoteAddr: str(pick('remote_addr', 'remoteAddr')),
    localPort: num(pick('local_port', 'localPort')),
    protocol: str(v.protocol),
    firmware: str(v.firmware),
    login: isRecord(login) ? (login as ConnectionLogin) : null,
    counters: counters(v.counters),
    previousDisconnectReason: str(pick('previous_disconnect_reason', 'previousDisconnectReason')),
    gapSincePreviousS: num(pick('gap_since_previous_s', 'gapSincePreviousS')),
  };
}

/** Boundary check of GET /api/v1/devices/:id/connections (`{ device_id?, items: DeviceConnection[] }`). */
export function parseDeviceConnections(v: unknown): { items: DeviceConnection[] } {
  if (!isRecord(v) || !Array.isArray(v.items)) throw new Error('invalid connections response');
  return { items: v.items.map(normalizeConnection) };
}

/** Connection history of ONE device, newest first. Fetched on mount and when `limit` changes; never globally. */
export const useDeviceConnections = (id: string, limit: number) =>
  useQuery({
    queryKey: ['device', id, 'connections', limit],
    queryFn: () =>
      request('GET', `/api/v1/devices/${id}/connections?limit=${limit}`, undefined, parseDeviceConnections),
    staleTime: 30_000,
  });
